/* eslint-disable */
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { HOST, PORT } from '../environment';
import { getClientAgent, getClientId } from '../hookServices/clientId';
import { setIpBlocked, parseIpBlockedFromResponse } from '../ipBlockState';

const OPEN_EMS_PUBLIC_URL = (
  (typeof process !== 'undefined' &&
    process.env.REACT_APP_OPEN_EMS_UI_URL &&
    String(process.env.REACT_APP_OPEN_EMS_UI_URL).trim()) ||
  'https://220-km.com:9220'
).replace(/\/+$/, '');

const rawBaseQuery = fetchBaseQuery({ baseUrl: `${HOST}:${PORT}` });

// Shared pause so polling does not keep nginx/Java 429 windows alive.
let rateLimitPauseUntil = 0;
const RATE_LIMIT_PAUSE_MS = 10_000;

const baseQueryWithIpBlock = async (args, api, extraOptions) => {
  if (Date.now() < rateLimitPauseUntil) {
    return { error: { status: 429, data: 'Too many requests' } };
  }
  const result = await rawBaseQuery(args, api, extraOptions);
  if (result.error) {
    const status = result.error.status;
    const data = result.error.data;
    if (status === 429) {
      rateLimitPauseUntil = Date.now() + RATE_LIMIT_PAUSE_MS;
    }
    const ip = parseIpBlockedFromResponse(status, data);
    if (ip !== null) {
      setIpBlocked(ip);
    }
  }
  return result;
};

export const api = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithIpBlock,
  // Tab focus used to refetch every mounted query at once and trip nginx 429.
  refetchOnFocus: false,
  endpoints: builder => ({
    deviceStart: builder.query({
      query: ({ stationNumber }) => ({
        url: `/api/device/v2/start?clientUiId=${getClientId()}`,
        method: 'post',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          station_number: stationNumber,
        }),
      }),
      keepUnusedDataFor: 20,
    }),
    //todo test properly autoStart=false on iphone
    generateCheckoutLinkKwt: builder.query({
      query: ({ stationNumber, kwt, autoStart }) => {
        const params = new URLSearchParams({
          station_number: stationNumber,
          kwt: Number(kwt),
          clientUiId: getClientId(),
          clientUiAgent: getClientAgent(),
          autoStart,
        });
        return `/api/order/generateCheckoutLinkKwt?${params.toString()}`;
      },
      keepUnusedDataFor: 1,
    }),
    skipPaymentLocal: builder.query({
      query: ({ stationNumber, kwt, autoStart }) => {
        const params = new URLSearchParams({
          station_number: stationNumber,
          kwt: Number(kwt),
          clientUiId: getClientId(),
          clientUiAgent: getClientAgent(),
          autoStart,
        });
        return `/api/order/skipPaymentLocal?${params.toString()}`;
      },
      keepUnusedDataFor: 1,
    }),
    getBloodyLitersSaved: builder.query({
      query: () => `api/device/v2/getBloodyLitersSaved`,
      // Rarely changes; avoid refetch storms against prod rate limits.
      keepUnusedDataFor: 300,
    }),
    getInProgressOrdersCount: builder.query({
      query: () => `/api/order/getInProgressOrdersCount?clientUiId=${getClientId()}`,
      keepUnusedDataFor: 30,
    }),

    getBalance: builder.query({
      query: () => `api/balance/getBalanceCents?clientUiId=${getClientId()}`,
      keepUnusedDataFor: 30,
    }),
    getActiveEnergyCards: builder.query({
      query: () => `/api/energy-card/active?clientUiId=${getClientId()}`,
      keepUnusedDataFor: 30,
    }),
    activateEnergyCard: builder.mutation({
      query: qrToken => ({
        url: `/api/energy-card/activate?qrToken=${encodeURIComponent(qrToken)}&clientUiId=${getClientId()}`,
        method: 'POST',
      }),
    }),
    getEcRemainingKwh: builder.query({
      query: ({ stationNumber }) =>
        `/api/energy-card/remaining-kwh?clientUiId=${getClientId()}&stationNumber=${encodeURIComponent(stationNumber)}`,
      keepUnusedDataFor: 1,
    }),
    getNearestStation: builder.query({
      query: ({ lat, lon, top, distanceM }) =>
        `api/device/v2/station/nearest?lat=${lat}&lon=${lon}&top=${top}&distance_m=${distanceM}&clientUiId=${getClientId()}`,
      keepUnusedDataFor: 1,
    }),
    getKm220Ports: builder.query({
      query: ({ acdc, freshOnly, chinaFilter }) => {
        const params = new URLSearchParams({ acdc });
        if (freshOnly) {
          params.append('freshOnly', 'true');
        }
        if (chinaFilter && chinaFilter !== 'all') {
          params.append('chinaFilter', chinaFilter);
        }
        return `api/device/v2/station/all?${params.toString()}`;
      },
      keepUnusedDataFor: 30,
    }),
    getTokaPorts: builder.query({
      query: ({ acdc, freshOnly, chinaFilter }) => {
        const params = new URLSearchParams({ acdc });
        if (freshOnly) {
          params.append('freshOnly', 'true');
        }
        if (chinaFilter && chinaFilter !== 'all') {
          params.append('chinaFilter', chinaFilter);
        }
        return `api/roaming/getTokaStations?${params.toString()}`;
      },
      keepUnusedDataFor: 1,
    }),
    getUgvPorts: builder.query({
      query: ({ acdc, freshOnly, chinaFilter }) => {
        const params = new URLSearchParams({ acdc });
        if (freshOnly) {
          params.append('freshOnly', 'true');
        }
        if (chinaFilter && chinaFilter !== 'all') {
          params.append('chinaFilter', chinaFilter);
        }
        return `api/roaming/getUgvStations?${params.toString()}`;
      },
      keepUnusedDataFor: 1,
    }),
    getEcofactorPorts: builder.query({
      query: () => `api/roaming/getEcofactorStations?clientUiId=${getClientId()}`,
      keepUnusedDataFor: 1,
    }),
    getG2YPorts: builder.query({
      query: () => `api/roaming/getG2YStations?clientUiId=${getClientId()}`,
      keepUnusedDataFor: 1,
    }),
    getEvua80KwStations: builder.query({
      query: () => 'api/roaming/evua/stations',
      // Align with Java Caffeine + Cache-Control max-age (24h).
      keepUnusedDataFor: 86400,
    }),
    getHistoryData: builder.query({
      query: () => `api/v2/history/getHistoryByClientUiId?clientUiId=${getClientId()}`,
      keepUnusedDataFor: 30,
    }),
    getHistoryPage: builder.query({
      query: ({ clientUiId, page = 0, pageSize = 30 }) =>
        `api/v2/history/getHistoryPageByClientUiId?clientUiId=${encodeURIComponent(clientUiId)}&page=${page}&pageSize=${pageSize}`,
      keepUnusedDataFor: 1,
    }),
    getHistoryTotals: builder.query({
      query: clientUiId => `api/v2/history/getHistoryTotalsByClientUiId?clientUiId=${encodeURIComponent(clientUiId)}`,
      keepUnusedDataFor: 60,
    }),
    getHistoryPowerCurve: builder.query({
      query: ({ clientUiId, jobId }) =>
        `api/v2/history/power-curve?clientUiId=${encodeURIComponent(clientUiId)}&jobId=${encodeURIComponent(jobId)}`,
      keepUnusedDataFor: 600,
    }),
    getStationPowerCurve: builder.query({
      query: ({ stationNumber }) =>
        `api/v2/history/power-curve-by-station?stationNumber=${encodeURIComponent(stationNumber)}`,
      keepUnusedDataFor: 15,
    }),
    downloadReceipt: builder.mutation({
      queryFn: async ({ invoiceId }, api, extraOptions, baseQuery) => {
        const isIos =
          ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(
            navigator.platform
          ) ||
          (navigator.userAgent.includes('Mac') && 'ontouchend' in document);

        if (isIos) {
          // iOS Safari blocks window.open() when called after await (user gesture expires ~0.5s).
          // Open window immediately while user gesture is active, then redirect after validation.
          const win = window.open('', '_blank', 'noopener,noreferrer');

          const result = await baseQuery({
            url: `/api/v2/history/getReceipt?invoiceId=${invoiceId}`,
            responseHandler: response => response.blob(),
          });
          if (result.error) {
            if (win) win.close();
            return { error: result.error };
          }
          const receiptUrl = `${window.location.origin}/api/v2/history/getReceipt?invoiceId=${encodeURIComponent(invoiceId)}`;
          if (win) {
            win.location.href = receiptUrl;
          } else {
            window.open(receiptUrl, '_blank', 'noopener,noreferrer');
          }
          return { data: null };
        }

        const result = await baseQuery({
          url: `/api/v2/history/getReceipt?invoiceId=${invoiceId}`,
          responseHandler: response => response.blob(),
        });

        if (result.error) {
          return { error: result.error };
        }

        const blob = new Blob([result.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `receipt_${invoiceId}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        return { data: null };
      },
    }),
    getDeviceStation: builder.query({
      query: ({ stationNumber, lat, lon }) =>
        `/api/device/v2/station?station_number=${stationNumber}&lat=${lat}&lon=${lon}&clientUiId=${getClientId()}`,
      keepUnusedDataFor: 1,
    }),
    getStationStatus: builder.query({
      query: ({ stationNumber }) =>
        `/api/device/v2/station/status?station_number=${stationNumber}&clientUiId=${getClientId()}`,
      keepUnusedDataFor: 1,
      providesTags: ['StationStatus'],
    }),
    getDeviceStatus: builder.query({
      query: ({ stationNumber }) => `/api/device/v2/device/status?station_number=${stationNumber}`,
      keepUnusedDataFor: 1,
    }),
    getTokaStationPhotos: builder.query({
      query: ({ stationNumber }) =>
        `/api/roaming/toka/station/photos?station_number=${encodeURIComponent(stationNumber)}`,
      keepUnusedDataFor: 300,
    }),
    getStationOnboardingPhotos: builder.query({
      query: ({ stationNumber }) => `/api/device/v2/station/photos?station_number=${encodeURIComponent(stationNumber)}`,
      keepUnusedDataFor: 300,
    }),
    getUserMe: builder.query({
      query: ({ userToken }) => ({
        url: `/user/me?clientUiId=${getClientId()}`,
        headers: {
          'X-Auth-Token': userToken,
        },
      }),
    }),
    getB2bStations: builder.query({
      query: ({ userToken }) => ({
        url: `/b2b/stations?clientUiId=${getClientId()}`,
        headers: {
          'X-Auth-Token': userToken,
        },
      }),
      transformResponse: response => ({
        stations: response
          ?.map(el => el.number)
          .sort((a, b) => a - b)
          .map(n => {
            return { label: n, value: n };
          }),
      }),
    }),
    getB2bReport: builder.query({
      query: ({ userToken, months, station }) => ({
        url: `/b2b/getReport?clientUiId=${getClientId()}`,
        method: 'post',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Auth-Token': userToken,
        },
        body: JSON.stringify({
          station_numbers: station,
          'months_yyyy-mm': months,
        }),
      }),
    }),
    getDayKwh: builder.query({
      query: ({ date } = {}) => {
        const params = new URLSearchParams();
        if (date) params.set('date', date);
        const q = params.toString();
        return { url: `/b2b/public/day-kwh${q ? `?${q}` : ''}` };
      },
      keepUnusedDataFor: 120,
    }),
    getOpenEmsLandingTotals: builder.query({
      query: () => ({
        url: `${OPEN_EMS_PUBLIC_URL}/api/power-flow/landing-totals`,
      }),
      keepUnusedDataFor: 300,
    }),
    getB2bPublicStats: builder.query({
      query: () => ({ url: '/b2b/public/stats' }),
      keepUnusedDataFor: 300,
    }),
    getB2bPublicRealtimePower: builder.query({
      query: ({ station } = {}) => {
        const params = new URLSearchParams();
        if (station != null && station !== '') params.set('station', String(station));
        const q = params.toString();
        return { url: `/b2b/public/realtime-power${q ? `?${q}` : ''}` };
      },
      keepUnusedDataFor: 10,
    }),
    getB2bPublicMinerPower: builder.query({
      query: () => ({ url: '/b2b/public/miner-power' }),
      keepUnusedDataFor: 25,
    }),
    userLogout: builder.query({
      query: ({ userToken }) => ({
        url: `/user/logout?clientUiId=${getClientId()}`,
        method: 'post',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Auth-Token': userToken,
        },
      }),
    }),
    stopProcessCharging: builder.query({
      query: ({ portNumber }) => ({
        url: '/api/device/v2/stop',
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ clientId: getClientId(), port: portNumber }),
      }),
    }),
    // Free test charge topup - adds balance for 1 kWh
    freeTestChargeTopup: builder.query({
      query: ({ stationNumber }) => ({
        url: `/api/balance/freeTestChargeTopup?clientUiId=${getClientId()}&station_number=${stationNumber}`,
        method: 'post',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Client-Ui-Agent': getClientAgent(),
        },
      }),
      keepUnusedDataFor: 1,
    }),
    // Notify support Telegram when user clicks Recover Balance
    notifyBalanceRecoverClicked: builder.mutation({
      query: clientUiId => ({
        url: `/api/balance/notifyBalanceRecoverClicked?clientUiId=${encodeURIComponent(clientUiId)}`,
        method: 'post',
        headers: {
          Accept: 'application/json',
        },
      }),
    }),
    // Notify support Telegram when user clicks refund link (заявка на повернення коштів)
    notifyRefundClicked: builder.mutation({
      query: clientUiId => ({
        url: `/api/balance/notifyRefundClicked?clientUiId=${encodeURIComponent(clientUiId)}`,
        method: 'post',
        headers: {
          Accept: 'application/json',
        },
      }),
    }),
    // Recover balance from bank receipt screenshot (OCR + order lookup)
    recoverBalanceFromReceipt: builder.mutation({
      query: ({ clientUiId, file }) => {
        const formData = new FormData();
        formData.append('receipt', file);
        return {
          url: `/api/balance/recoverBalanceFromReceipt?clientUiId=${encodeURIComponent(clientUiId)}`,
          method: 'post',
          body: formData,
          headers: {
            Accept: 'application/json',
            'X-Client-Ui-Agent': getClientAgent(),
          },
        };
      },
    }),
    restartStation: builder.mutation({
      query: ({ stationNumber }) => ({
        url: `/api/device/v2/station/restart?station_number=${encodeURIComponent(stationNumber)}&clientUiId=${encodeURIComponent(getClientId())}`,
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
      }),
    }),
    createReservation: builder.mutation({
      query: ({ stationNumber }) => ({
        url: `/api/reservation/create?station_number=${encodeURIComponent(stationNumber)}&clientUiId=${encodeURIComponent(getClientId())}`,
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Client-Ui-Agent': getClientAgent(),
        },
      }),
      invalidatesTags: ['StationStatus', 'ReservationDailyLimit'],
    }),
    cancelReservation: builder.mutation({
      query: ({ stationNumber }) => ({
        url: `/api/reservation/cancel?station_number=${encodeURIComponent(stationNumber)}&clientUiId=${encodeURIComponent(getClientId())}`,
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
        },
      }),
      invalidatesTags: ['StationStatus', 'ReservationDailyLimit'],
    }),
    getReservationDailyLimit: builder.query({
      query: () => `/api/reservation/daily-limit?clientUiId=${encodeURIComponent(getClientId())}`,
      providesTags: ['ReservationDailyLimit'],
      keepUnusedDataFor: 30,
    }),
  }),
});

export const {
  useGetNearestStationQuery,
  useGetBloodyLitersSavedQuery,
  useGetBalanceQuery,
  useGetActiveEnergyCardsQuery,
  useActivateEnergyCardMutation,
  useGetEcRemainingKwhQuery,
  useGetInProgressOrdersCountQuery,
  useGetKm220PortsQuery,
  useGetTokaPortsQuery,
  useGetUgvPortsQuery,
  useGetEcofactorPortsQuery,
  useGetG2YPortsQuery,
  useGetEvua80KwStationsQuery,
  useLazyGetEvuaStationPhotosQuery,
  useGetDeviceStationQuery,
  useGetStationStatusQuery,
  useGetDeviceStatusQuery,
  useGetTokaStationPhotosQuery,
  useGetStationOnboardingPhotosQuery,
  useGetUserMeQuery,
  useGetB2bStationsQuery,
  useGetB2bReportQuery,
  useGetB2bPublicStatsQuery,
  useGetB2bPublicRealtimePowerQuery,
  useGetB2bPublicMinerPowerQuery,
  useGetDayKwhQuery,
  useGetOpenEmsLandingTotalsQuery,
  useGetHistoryDataQuery,
  useGetHistoryPageQuery,
  useGetHistoryTotalsQuery,
  useGetHistoryPowerCurveQuery,
  useGetStationPowerCurveQuery,
  useDownloadReceiptMutation,
  useNotifyBalanceRecoverClickedMutation,
  useNotifyRefundClickedMutation,
  useRecoverBalanceFromReceiptMutation,
  useRestartStationMutation,
  useCreateReservationMutation,
  useCancelReservationMutation,
  useGetReservationDailyLimitQuery,
  useLazyGetReservationDailyLimitQuery,
  useStopProcessChargingQuery,
  useLazyFreeTestChargeTopupQuery,
} = api;
